import { useRef } from 'react';
import { useSessionStorageState } from 'ahooks';
import type { Location } from 'history';

/**
 * This is for automated tests to disable certain aspects (marketing popups)
 * on the application by reading from the session storage.
 *
 * Sets the value a single time on mount, and does not update it.
 *
 * Location is injected into the hook due to dependency resolution issues that we face with
 * conflicting v5 and v6 react-router packages used between the apps.
 */
function createProgrammaticDisable(
    key: string,
): (location?: Location) => boolean {
    function useProgrammaticDisable(location?: Location) {
        const searchParams = useRef(
            new URLSearchParams(location?.search || ''),
        );

        const [disabledBySessionStorage, setSessionStorage] =
            useSessionStorageState(key, {
                defaultValue: false,
            });

        const disabledByRoute = (): boolean => {
            if (searchParams.current.has(key)) {
                // Persist it if we're in the browser. Don't want to force automations to keep the
                // search param after every request/navigation.
                if (!disabledBySessionStorage) {
                    setSessionStorage(true);
                }

                return true;
            }

            return false;
        };

        const { current: disabled } = useRef(
            disabledByRoute() || disabledBySessionStorage,
        );

        return disabled;
    }

    return useProgrammaticDisable;
}

export default createProgrammaticDisable;
